/**
 * Credit Warning System Utilities
 *
 * Centralized logic for credit warnings and thresholds.
 * Used across the frontend to provide consistent warning behavior.
 */

export const CREDIT_THRESHOLDS = {
  CRITICAL: 10,
  LOW: 30,
  HEALTHY: 30
} as const;

export type CreditWarningLevel = 'healthy' | 'low' | 'critical';

export interface CreditWarningInfo {
  title: string;
  message: string;
  variant: 'warning' | 'destructive';
}

/**
 * Determine the warning level based on current credit balance
 */
export function getCreditWarningLevel(credits: number): CreditWarningLevel {
  if (credits < CREDIT_THRESHOLDS.CRITICAL) return 'critical';
  if (credits < CREDIT_THRESHOLDS.LOW) return 'low';
  return 'healthy';
}

/**
 * Get warning message and styling information for a given credit balance
 *
 * @param credits - Current credit balance
 * @param userType - Type of user (individual or practitioner)
 * @returns Warning information object or null if no warning needed
 */
export function getCreditWarningMessage(
  credits: number,
  userType: 'individual' | 'practitioner' = 'individual'
): CreditWarningInfo | null {
  const level = getCreditWarningLevel(credits);

  if (level === 'critical') {
    return {
      title: 'Critical: Very Low Credits',
      message: userType === 'practitioner'
        ? `Only ${credits} credits remaining. You may not be able to create sessions for your clients.`
        : `Only ${credits} credits remaining. Purchase more to continue creating sessions.`,
      variant: 'destructive'
    };
  }

  if (level === 'low') {
    return {
      title: 'Low Credits',
      message: `You have ${credits} credits remaining. Consider purchasing more to avoid interruptions.`,
      variant: 'warning'
    };
  }

  return null;
}

/**
 * Check if user can afford a specific action
 *
 * @param credits - Current credit balance
 * @param required - Credits required for the action
 * @returns True if user has enough credits
 */
export function canAffordAction(credits: number, required: number): boolean {
  return credits >= required;
}

/**
 * Calculate shortfall if user cannot afford an action
 *
 * @param credits - Current credit balance
 * @param required - Credits required for the action
 * @returns Number of credits short, or 0 if can afford
 */
export function calculateShortfall(credits: number, required: number): number {
  return Math.max(0, required - credits);
}

/**
 * Estimate how many sessions remaining based on average credits per session
 *
 * @param credits - Current credit balance
 * @param avgCreditsPerSession - Average credits consumed per session (default: 5)
 * @returns Estimated number of sessions remaining
 */
export function estimateSessionsRemaining(
  credits: number,
  avgCreditsPerSession: number = 5
): number {
  return Math.floor(credits / avgCreditsPerSession);
}
